// sim/geneForge.ts — the procedural gene GENERATOR and the DROP system.
//
// THE FORGE (forgeGene) is the only place genes are minted procedurally. Given a
// source (a slain rival's caste, a dead member's caste, or an event sentinel) it:
//   1) picks a base body-part TYPE — weighted by the source caste's affinity
//      (CASTE_TYPE_WEIGHT) so a Soldier tends to drop Mandible/Carapace, etc.;
//      uniform when there is no caste source;
//   2) rolls RARITY as the best of (1 + qualityBonus) weighted draws — integer
//      "luck": harder-won kills (combat) get extra draws, never floats;
//   3) inserts the gene "item object" (an integer-id row in GeneInventory);
//   4) rolls RARITY_TRAIT_ROLLS[rarity] traits — repeats STACK naturally via
//      addTrait, so rarity directly drives both trait COUNT and stacking.
//
// THE DROP SYSTEM routes three world sources into the forge:
//   - rollCombatDrop — a kill yields loot to the KILLER's colony (qualityBonus 1);
//   - rollDeathDrop  — a natural death salvages loot to the member's OWN colony;
//   - awardGeneFromEvent — a generic event grant (caller owns the probability).
//
// All functions are deterministic (consume the passed Rng only — never
// Math.random) and allocation-free: weighted picks use a sum-then-walk over the
// data tables with no temp arrays. Acyclic imports: geneForge → genes +
// geneData + types.

import { NIL, GENE_TYPE_COUNT, GENE_TRAIT_COUNT, RARITY_COUNT, CASTE_COUNT } from '../types.ts';
import type { GeneConfig } from '../types.ts';
import type { Rng } from '../rng.ts';
import type { GeneInventory, GeneId } from './genes.ts';
import { CASTE_TYPE_WEIGHT, RARITY_WEIGHT, RARITY_TRAIT_ROLLS } from './geneData.ts';

/**
 * Sentinel sourceCaste for genes minted by world events (not looted from a
 * caste). Negative so accessors/describe can distinguish it from a real caste.
 */
export const GENE_SOURCE_EVENT = -1;

/**
 * Weighted pick of a base gene type for `sourceCaste`. Sum-then-walk over the
 * caste's affinity row (no allocation). Uniform fallback when there is no real
 * caste (event drops, or an out-of-range caste).
 */
function pickBaseType(rng: Rng, sourceCaste: number): number {
  if (sourceCaste < 0 || sourceCaste >= CASTE_COUNT) {
    return rng.int(GENE_TYPE_COUNT);
  }
  const row = CASTE_TYPE_WEIGHT[sourceCaste] as readonly number[];
  let total = 0;
  for (let i = 0; i < GENE_TYPE_COUNT; i++) total += row[i] as number;
  let r = rng.int(total);
  for (let i = 0; i < GENE_TYPE_COUNT; i++) {
    r -= row[i] as number;
    if (r < 0) return i;
  }
  return GENE_TYPE_COUNT - 1; // unreachable for total>0; defensive.
}

/** One weighted rarity draw via sum-then-walk over RARITY_WEIGHT. */
function rollRarityOnce(rng: Rng): number {
  let total = 0;
  for (let i = 0; i < RARITY_COUNT; i++) total += RARITY_WEIGHT[i] as number;
  let r = rng.int(total);
  for (let i = 0; i < RARITY_COUNT; i++) {
    r -= RARITY_WEIGHT[i] as number;
    if (r < 0) return i;
  }
  return RARITY_COUNT - 1; // defensive.
}

/**
 * Forge the BARE gene — pick base type, roll rarity (best of 1+qualityBonus draws), and
 * insert the item-object row — but add NO traits. Returns the new GeneId (or NIL at
 * capacity) and bumps forgedTotal on success. `forgeGene` layers the standard trait rolls
 * on top; the relative-loot path (web/evolutionBox.ts) instead fits the traits to a
 * power window, so it shares this exact type/rarity/insert step. Deterministic.
 */
export function forgeBareGene(
  inv: GeneInventory,
  rng: Rng,
  owner: number,
  originColony: number,
  sourceCaste: number,
  tick: number,
  qualityBonus: number,
): GeneId {
  // 1) base type.
  const type = pickBaseType(rng, sourceCaste);

  // 2) rarity: best of (1 + qualityBonus) weighted draws. The full draw count is
  // always consumed so the RNG stream is independent of how many improve the max
  // (keeps determinism simple and order-stable).
  let rarity = rollRarityOnce(rng);
  const extra = qualityBonus > 0 ? qualityBonus : 0;
  for (let k = 0; k < extra; k++) {
    const r = rollRarityOnce(rng);
    if (r > rarity) rarity = r;
  }

  // 3) insert the item-object row.
  const id = inv.insert(owner, type, rarity, originColony, sourceCaste, tick);
  if (id === NIL) return NIL;
  inv.forgedTotal++;
  return id;
}

/**
 * THE GENERATOR. Mint one gene for `owner` from `sourceCaste` at `tick`.
 * `qualityBonus` adds extra rarity draws (best-of-(1+qualityBonus), integer
 * luck). Returns the new GeneId, or NIL if the colony was at capacity.
 * Deterministic + allocation-free. Bumps forgedTotal on success.
 */
export function forgeGene(
  inv: GeneInventory,
  rng: Rng,
  owner: number,
  originColony: number,
  sourceCaste: number,
  tick: number,
  qualityBonus: number,
): GeneId {
  const id = forgeBareGene(inv, rng, owner, originColony, sourceCaste, tick, qualityBonus);
  if (id === NIL) return NIL;

  // 4) trait rolls — repeats STACK naturally (addTrait increments the count).
  const rolls = RARITY_TRAIT_ROLLS[inv.rarityOf(id) as number] as number;
  for (let k = 0; k < rolls; k++) {
    const trait = rng.int(GENE_TRAIT_COUNT);
    inv.addTrait(id, trait, 1);
  }
  return id;
}

/**
 * Combat drop: when `killerColony` slays a member of `victimColony`, roll for a
 * gene to the KILLER's colony. Combat loot gets qualityBonus 1 (hard-won kills
 * yield better odds). No-op (NIL) if there is no killer colony or the roll fails.
 * combatDropsTotal++ on a non-NIL forge.
 */
export function rollCombatDrop(
  inv: GeneInventory,
  rng: Rng,
  killerColony: number,
  victimColony: number,
  victimCaste: number,
  tick: number,
  config: GeneConfig,
  scalePct = 100,
): GeneId {
  if (killerColony < 0) return NIL;
  // Event-scaled chance (scalePct/100; 100 ⇒ unchanged). CRITICAL: keep EXACTLY
  // ONE rng.int(100) draw regardless of scale, so the RNG stream is identical at
  // neutral and the scale only shifts the comparison threshold.
  const effective = Math.floor((config.combatDropChancePct * scalePct) / 100);
  if (rng.int(100) < effective) {
    const id = forgeGene(inv, rng, killerColony, victimColony, victimCaste, tick, 1);
    if (id !== NIL) inv.combatDropsTotal++;
    return id;
  }
  return NIL;
}

/**
 * Death/salvage drop: a naturally-dead member may salvage a gene to its OWN
 * colony (qualityBonus 0). No-op (NIL) on a failed roll. deathDropsTotal++ on a
 * non-NIL forge.
 */
export function rollDeathDrop(
  inv: GeneInventory,
  rng: Rng,
  colony: number,
  caste: number,
  tick: number,
  config: GeneConfig,
  scalePct = 100,
): GeneId {
  // Event-scaled chance (scalePct/100; 100 ⇒ unchanged). CRITICAL: keep EXACTLY
  // ONE rng.int(100) draw regardless of scale (determinism — see rollCombatDrop).
  const effective = Math.floor((config.deathDropChancePct * scalePct) / 100);
  if (rng.int(100) < effective) {
    const id = forgeGene(inv, rng, colony, colony, caste, tick, 0);
    if (id !== NIL) inv.deathDropsTotal++;
    return id;
  }
  return NIL;
}

/**
 * Generic EVENT grant: always forges a gene to `colony` (the CALLER decides
 * probability). Source is the event sentinel. For non-death world events and for
 * tests that need a deterministic, unconditional grant. eventDropsTotal++ on a
 * non-NIL forge.
 */
export function awardGeneFromEvent(
  inv: GeneInventory,
  rng: Rng,
  colony: number,
  tick: number,
  qualityBonus: number,
): GeneId {
  const id = forgeGene(inv, rng, colony, colony, GENE_SOURCE_EVENT, tick, qualityBonus);
  if (id !== NIL) inv.eventDropsTotal++;
  return id;
}
